import { Link, useLocation, useRoute } from 'wouter'
import { Avatar } from '../../components/Avatar'
import { Button } from '../../components/Button'
import { Card } from '../../components/Card'
import { useAuth } from '../../hooks/useAuth'
import { ROUTES } from '../../routes'
import { styled } from '../../setup'
import { borders, colors, spacing, typography } from '../../theme'

const BackLink = styled('a')`
  display: inline-flex;
  align-items: center;
  gap: ${spacing.sm};
  color: ${colors.textMuted};
  font-size: ${typography.fontSizeBase};
  text-decoration: none;
  margin-bottom: ${spacing.lg};
  transition: color 0.2s;

  &:hover {
    color: ${colors.textSecondary};
  }
`

const ProfileHeader = styled('div')`
  display: flex;
  align-items: center;
  gap: ${spacing.lg};
  margin-bottom: ${spacing['2xl']};
`

const ProfileInfo = styled('div')`
  flex: 1;
`

const DisplayName = styled('h1')`
  color: ${colors.textPrimary};
  font-size: ${typography.fontSize2xl};
  font-weight: ${typography.fontWeightSemibold};
  margin: 0 0 ${spacing.sm} 0;
`

const MetaRow = styled('div')`
  display: flex;
  align-items: center;
  gap: ${spacing.md};
  flex-wrap: wrap;
`

const Badge = styled('span')<{ variant: 'success' | 'warning' | 'info' }>`
  display: inline-block;
  padding: ${spacing.xs} ${spacing.sm};
  border-radius: ${borders.radius};
  font-size: 12px;
  font-weight: ${typography.fontWeightMedium};
  text-transform: uppercase;
  letter-spacing: 0.03em;

  background: ${(props) =>
    ({
      success: colors.successBg,
      warning: 'rgba(251, 191, 36, 0.15)',
      info: 'rgba(59, 130, 246, 0.15)',
    })[props.variant]};

  color: ${(props) =>
    ({
      success: colors.success,
      warning: '#b45309',
      info: '#1d4ed8',
    })[props.variant]};
`

const Section = styled('section')`
  margin-bottom: ${spacing['2xl']};
`

const SectionTitle = styled('h2')`
  color: ${colors.textMuted};
  font-size: ${typography.fontSizeSm};
  font-weight: ${typography.fontWeightMedium};
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin: 0 0 ${spacing.md} 0;
`

const InfoGrid = styled('div')`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: ${spacing.lg};
`

const InfoItem = styled('div')`
  background: ${colors.bgSurfaceAlt};
  padding: ${spacing.lg};
  border-radius: ${borders.radius};
`

const InfoLabel = styled('div')`
  color: ${colors.textMuted};
  font-size: ${typography.fontSizeSm};
  margin-bottom: ${spacing.xs};
`

const InfoValue = styled('div')`
  color: ${colors.textPrimary};
  font-size: ${typography.fontSizeBase};
  font-weight: ${typography.fontWeightMedium};
`

const NotFound = styled('div')`
  text-align: center;
  padding: ${spacing['4xl']} ${spacing['2xl']};
`

const NotFoundTitle = styled('h2')`
  color: ${colors.textPrimary};
  font-size: ${typography.fontSizeXl};
  font-weight: ${typography.fontWeightSemibold};
  margin: 0 0 ${spacing.md} 0;
`

const NotFoundText = styled('p')`
  color: ${colors.textMuted};
  font-size: ${typography.fontSizeBase};
  margin: 0 0 ${spacing['2xl']} 0;
`

function formatRole(role: string): string {
  switch (role) {
    case 'FULL_GUARDIAN':
      return 'Full Guardian'
    case 'LIMITED_GUARDIAN':
      return 'Limited Guardian'
    case 'VIEW_ONLY':
      return 'View Only'
    default:
      return role.replace(/_/g, ' ')
  }
}

function formatKind(kind: string): string {
  switch (kind) {
    case 'PARENT':
      return 'Parent'
    case 'STEPPARENT':
      return 'Stepparent'
    case 'GUARDIAN':
      return 'Guardian'
    case 'SIBLING':
      return 'Sibling'
    case 'OTHER':
      return 'Other'
    default:
      return kind
  }
}

function formatLinkState(state: string | null): string {
  switch (state) {
    case 'LINKED':
      return 'Active'
    case 'PENDING':
      return 'Pending'
    case 'UNLINKED':
      return 'Unlinked'
    default:
      return 'Unknown'
  }
}

export function ChildDetailPage() {
  const { managedChildren } = useAuth()
  const [, setLocation] = useLocation()
  const [, params] = useRoute(ROUTES.GUARDIAN_CHILD)
  const childId = params?.childId

  // Find the child
  const child = managedChildren.value.find((c) => c.id === childId)

  if (!child) {
    return (
      <Card>
        <NotFound>
          <NotFoundTitle>Child not found</NotFoundTitle>
          <NotFoundText>This child may not be linked to your account.</NotFoundText>
          <Button variant="primary" onClick={() => setLocation(ROUTES.GUARDIAN_DASHBOARD)}>
            Back to Dashboard
          </Button>
        </NotFound>
      </Card>
    )
  }

  return (
    <>
      <Link href={ROUTES.GUARDIAN_DASHBOARD}>
        <BackLink>&larr; Back to Children</BackLink>
      </Link>

      <Card>
        <ProfileHeader>
          <Avatar name={child.displayName} size={80} />
          <ProfileInfo>
            <DisplayName>{child.displayName}</DisplayName>
            <MetaRow>
              <Badge variant="info">{formatKind(child.guardianKind)}</Badge>
              <Badge variant="success">{formatLinkState(child.childLinkState)}</Badge>
            </MetaRow>
          </ProfileInfo>
        </ProfileHeader>

        <Section>
          <SectionTitle>Your Access</SectionTitle>
          <InfoGrid>
            <InfoItem>
              <InfoLabel>Relationship</InfoLabel>
              <InfoValue>{formatKind(child.guardianKind)}</InfoValue>
            </InfoItem>
            <InfoItem>
              <InfoLabel>Permission Level</InfoLabel>
              <InfoValue>{formatRole(child.accessRole)}</InfoValue>
            </InfoItem>
            <InfoItem>
              <InfoLabel>Link Status</InfoLabel>
              <InfoValue>{formatLinkState(child.childLinkState)}</InfoValue>
            </InfoItem>
          </InfoGrid>
        </Section>

        <Section>
          <SectionTitle>Actions</SectionTitle>
          <Button variant="secondary" onClick={() => setLocation(ROUTES.GUARDIAN_REQUESTS)}>
            View Pending Requests
          </Button>
        </Section>
      </Card>
    </>
  )
}
